import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import styles from './Digiko.module.css';
import flutterVideo from '../../../images/digiko.mp4';

const DigikoProjectShowcase = () => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const [hoveredResponsibility, setHoveredResponsibility] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const responsibilities = [
    { text: 'Idea Generation and Conceptualization', icon: '💡' },
    { text: 'User Experience (UX) and Interface (UI) Design', icon: '🎯' },
    { text: 'Technology Integration', icon: '⚙️' },
    { text: 'Web Design and Development', icon: '🌐' },
    { text: 'Digital Marketing Strategies', icon: '📊' },
    { text: 'Brand Management and Identity', icon: '🎨' },
    { text: 'Continuous Innovation', icon: '🚀' }
  ];

  const tools = [
    { name: 'WordPress', icon: '📝', description: 'CMS Platform' },
    { name: 'Figma', icon: '🎨', description: 'Design Tool' },
    { name: 'Photoshop', icon: '🖼️', description: 'Image Editor' },
    { name: 'Illustrator', icon: '✨', description: 'Vector Graphics' },
    { name: 'CorelDraw', icon: '🎭', description: 'Graphic Design' }
  ];

  const skills = [
    'CSS', 'Figma', 'HTML5', 'Svelte', 'Flutter', 
    'CorelDraw', 'WordPress', 'JavaScript', 'Tailwind CSS', 'Adobe Animate'
  ];

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
        {/* Animated Background */}
        <div className={styles.backgroundGradient}>
          <div className={styles.gradientOrb1}></div>
          <div className={styles.gradientOrb2}></div>
          <div className={styles.gradientOrb3}></div>
        </div>

        <div className={styles.contentWrapper}>
          {/* Hero Section */}
          <section className={styles.heroSection}>
            <div className={styles.heroContent}>
              <div className={styles.heroLeft}>
                <div className={styles.projectBadge}>
                  <span className={styles.badgeDot}></span>
                  <span>Ongoing Project</span>
                </div>
                
                <h1 className={styles.projectTitle}>Web & Marketing Solutions Platform</h1>
                <p className={styles.projectSubtitle}>End-to-End Digital Transformation</p>
                
                <div className={styles.projectMeta}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Work</span>
                    <span className={styles.metaValue}>Website Design</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Year</span>
                    <span className={styles.metaValue}>2020</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Duration</span>
                    <span className={styles.metaValue}>Jan 2020 - Ongoing</span>
                  </div>
                </div>
                
                <p className={styles.heroDescription}>
                  Creative technologists optimizing UX with cutting-edge solutions for branding, web, apps, marketing, and design. A comprehensive digital transformation and digital marketing agency delivering innovative solutions.
                </p>
                
                
              </div>
              
              <div className={styles.heroRight}>
                <div className={styles.heroVisual}>
                  <video 
                  className={styles.heroVideo}
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                >
                  <source src={flutterVideo} type="video/mp4" />
                </video>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Introduction</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
               Web & Marketing Solutions Platform that enhances user experiences and delivers tailored solutions to help businesses reach their full potential. It brings together brand management, responsive websites, mobile and enterprise applications, email campaigns, digital marketing, social media management, logo design, and presentation design into one unified ecosystem. By combining creativity with advanced technology, the platform continuously evolves to deliver high-quality web design, application development, and eCommerce solutions—focused on driving value, growth, and seamless experiences across all digital touchpoints.
              </p>
            </div>
          </section>

          {/* My Role Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>My Role</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                My role revolves around merging innovative ideas with cutting-edge technology to deliver bespoke creative solutions that elevate organizations to their full potential. In a world where seamless digital experiences are crucial, I am dedicated to crafting solutions across various domains, including Brand Management, Responsive Websites, Mobile Applications, Enterprise Applications, Email and Marketing Campaigns, Digital Marketing, Social Media Profile Management, as well as Logo Design and Company Presentations.
              </p>
            </div>
          </section>

          {/* Responsibilities Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Responsibilities</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.responsibilitiesGrid}>
              {responsibilities.map((responsibility, index) => (
                <div 
                  key={index}
                  className={styles.responsibilityCard}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onMouseEnter={() => setHoveredResponsibility(index)}
                  onMouseLeave={() => setHoveredResponsibility(null)}
                >
                  <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                  <div className={styles.cardIcon}>{responsibility.icon}</div>
                  <div className={styles.cardText}>{responsibility.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Tools Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Tools Used</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.toolsGrid}>
              {tools.map((tool, index) => (
                <div 
                  key={index}
                  className={styles.toolCard}
                  style={{ animationDelay: `${index * 0.15}s` }}
                  onMouseEnter={() => setHoveredTool(index)}
                  onMouseLeave={() => setHoveredTool(null)}
                >
                  <div className={styles.toolGlow}></div>
                  <div className={styles.toolIcon}>{tool.icon}</div>
                  <div className={styles.toolName}>{tool.name}</div>
                  <div className={styles.toolDescription}>{tool.description}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Skills Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Technologies & Skills</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.skillsContainer}>
              {skills.map((skill, index) => (
                <div 
                  key={index}
                  className={styles.skillTag}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <span className={styles.skillHash}>#</span>
                  {skill}
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer  />
      </div>
    </>
  );
};

export default DigikoProjectShowcase;